import React, { useEffect, useState } from "react";
import { EditableComponent } from "./component/EditableComponent/EditableComponent";
import { useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDispatch, useSelector } from "react-redux";
import { setMedicalTemplate } from "../../redux/reducers/medicalRecordSlice";
import { RootState } from "../../redux/store";
import {
  IMedicalTemplate,
  IStep,
  StepProps,
} from "../../interface/MedicalRecord";
import stepIcon from "../../assets/images/icons/step-icon.svg";
import activeStepIcon from "../../assets/images/icons/active-step.svg";
import completedStepIcon from "../../assets/images/icons/completed-step.svg";
import { DragAndDropItem } from "../../components";
import { getFirstVisibleSubStepIndex } from "./utils";

const MultiStepProgress: React.FC<StepProps> = ({
  Steps,
  currentStep,
  prevStep,
  currentSubStep,
  onStepClick,
  onSubStepClick,
  setLocalSteps,
}) => {
  const { type, PatientId } = useParams();
  const isCustomizeMode = type === "customize";

  const sensors = useSensors(useSensor(PointerSensor));

  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const dispatch = useDispatch();
  const medicalRecordTemplate = useSelector(
    (state: RootState) => state.medicalRecord.template,
  );

  const handleStepsReorder = React.useCallback(
    (reorderedSteps: IStep[]) => {
      setLocalSteps(reorderedSteps);

      const updatedTemplate = {
        ...medicalRecordTemplate,
        Steps: reorderedSteps,
      };
      dispatch(setMedicalTemplate(updatedTemplate as IMedicalTemplate));
    },
    [medicalRecordTemplate, dispatch],
  );

  const stepItems =
    Steps?.map((step) => ({
      ...step,
      id: step.Id.toString(),
    })) || [];

  const handleDragEnd = (event: DragEndEvent) => {
    if (!isCustomizeMode || !handleStepsReorder) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = stepItems.findIndex((item) => item.id === active.id);
    const newIndex = stepItems.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedItems = arrayMove(stepItems, oldIndex, newIndex);

      const updatedSteps = reorderedItems.map((item, index) => ({
        ...item,
        Order: index + 1,
      }));

      handleStepsReorder(updatedSteps);
    }
  };

  const renderStep = (step: any, stepIdx?: number) => {
    const actualStepIdx =
      stepIdx !== undefined
        ? stepIdx
        : stepItems.findIndex((s) => s.id === step.id);
    const isCurrentStep = currentStep === actualStepIdx;
    const isCompleted =
      completedSteps?.includes(actualStepIdx) && !isCurrentStep;

    const hasSubSteps = (step?.InverseParentSteps?.length ?? 0) > 0;

    const styleText = isCurrentStep
      ? "font-bold text-white"
      : isCompleted
        ? "text-primary-textFont"
        : "text-primary-textFont";

    const parentNode = (
      <div className="flex w-full items-center">
        <div
          onClick={() => {
            onStepClick?.(actualStepIdx);

            const firstVisibleSubIdx = getFirstVisibleSubStepIndex(
              step?.InverseParentSteps,
            );
            onSubStepClick?.(firstVisibleSubIdx);
          }}

          className={`flex w-full cursor-pointer flex-col ${step.InverseParentSteps?.length && isCurrentStep && !isCustomizeMode
              ? "-ms-1.5 rounded-2xl border bg-primary p-1.5 pb-3"
              : ""
            }`}
        >
          {/* Parent Step Title */}
          <div
            className={`flex w-full items-center gap-2 ${!isCustomizeMode ? "-ms-1.5 p-1.5" : ""} ${isCurrentStep &&
                !isCustomizeMode &&
                !step.InverseParentSteps?.length
                ? "rounded-full border bg-primary !text-white"
                : ""
              }`}
          >
            <img
              src={
                isCompleted
                  ? completedStepIcon
                  : isCurrentStep
                    ? activeStepIcon
                    : stepIcon
              }
              alt="step"
            />
            <span className={`text-sm ${styleText} flex-1`}>{step?.Name}</span>
          </div>

          {/* Connector line if needed */}
          {!hasSubSteps &&
            actualStepIdx !== stepItems.length - 1 &&
            !isCustomizeMode && (
              <div
                className={`-mb-1.5 ms-3.5 h-4 w-0.5 ${isCompleted ? "bg-primary" : "bg-primary-textFont"
                  }`}
              />
            )}

          {/* Sub Steps */}
          {hasSubSteps && (
            <div
              className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${isCurrentStep ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
            >
              {step?.InverseParentSteps?.map((sub: any, subIdx: number) => {
                const isSubCurrent = isCurrentStep && currentSubStep === subIdx;
                const isSubCompleted =
                  currentStep > actualStepIdx ||
                  (isCurrentStep && currentSubStep > subIdx);

                const style = isSubCurrent
                  ? " font-semibold text-white"
                  : isSubCompleted
                    ? "border-primary text-primary !text-white"
                    : "border-primary-textFont text-primary-textFont !text-white";

                const subNode = (
                  <div
                    key={sub?.Id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStepClick?.(actualStepIdx);
                      onSubStepClick?.(subIdx);
                    }}
                    className={`-mb-1 w-full cursor-pointer py-1.5 ps-6 text-sm ${style}`}
                  >
                    {sub.Name}
                  </div>
                );

                return isCustomizeMode ? (
                  <EditableComponent
                    key={sub?.Id}
                    name={`id_${step.Id}.InverseParentSteps[id_${sub?.Id}].IsVisible`}
                    component={subNode}
                    field={sub}
                    opacity
                  />
                ) : (
                  sub?.IsVisible && subNode
                );
              })}
            </div>
          )}
        </div>
      </div>
    );

    const wrappedNode = isCustomizeMode ? (
      <EditableComponent
        key={step.Id}
        name={`id_${step.Id}.IsVisible`}
        className={`w-full !flex-row rounded-3xl p-1.5 ${hasSubSteps ? "!items-start" : ""} ${isCurrentStep ? "border bg-primary" : ""}`}
        field={step}
        component={parentNode}
        opacity
      />
    ) : (
      step?.IsVisible && (
        <div className="w-full" key={step.Id}>
          {parentNode}
        </div>
      )
    );

    return wrappedNode;
  };

  useEffect(() => {
    setCompletedSteps((prev: any) => [...prev, prevStep]);
  }, [prevStep]);
  useEffect(() => {
    setCompletedSteps([]);
  }, [isCustomizeMode]);

  return (
    <div
      className={`flex ${PatientId ? "h-[calc(100dvh-12.18rem)]" : "h-[calc(100dvh-7rem)]"} bg-primary-bg !w-80 !max-w-80 flex-col items-start gap-1.5 overflow-y-auto rounded-xl p-4`}
    >
      {isCustomizeMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={stepItems}
            strategy={verticalListSortingStrategy}
          >
            {stepItems.map(
              (step, stepIdx) =>
                !step?.IsVisit && (
                  <DragAndDropItem key={step.id} id={step.id}>
                    {renderStep(step, stepIdx)}
                  </DragAndDropItem>
                ),
            )}
          </SortableContext>
        </DndContext>
      ) : (
        stepItems.map((step, stepIdx) => (
          <div key={step.Id} className="w-full">
            {renderStep(step, stepIdx)}
          </div>
        ))
      )}
    </div>
  );
};

export default MultiStepProgress;
